import {Injectable} from '@angular/core';
import {HttpClient, HttpParams, HttpResponse} from '@angular/common/http';
import {BehaviorSubject, Observable} from 'rxjs';
import {switchMap, tap} from 'rxjs/operators';
import {Globals} from '../../global/globals';
import {CurrentUserService} from '../user/current-user.service';
import {
  ExportSection,
  Folder,
  FolderExportRequest,
  FolderItem,
  FolderItemType,
  FolderPage,
  FolderPermission,
  FolderSave,
  FolderScope,
  FolderShare,
  FolderSort,
  FolderUsage,
  SimpleFolder
} from '../../dtos/folder/folder';

export interface FolderSearch {
  scope?: FolderScope;
  name?: string;
  page?: number;
  size?: number;
  sort?: FolderSort;
  order?: 'asc' | 'desc';
}

@Injectable({
  providedIn: 'root'
})
export class FolderService {

  /** last known number of lists shared with the user and not accepted yet, e.g. for the badge in the header */
  readonly pendingCount$ = new BehaviorSubject<number>(0);

  private usersBaseUri: string = this.globals.backendUri + '/users/';
  private exportBaseUri: string = this.globals.backendUri + '/exports/folders';
  private usageUri: string = this.globals.backendUri + '/folders/usage';

  constructor(private httpClient: HttpClient, private globals: Globals, private currentUserService: CurrentUserService) {
  }

  /* -------------------- lists -------------------- */

  getFolders(search: FolderSearch = {}): Observable<FolderPage> {
    return this.withUri('', uri => this.httpClient.get<FolderPage>(uri, {params: this.searchParams(search)}));
  }

  getPendingFolders(search: FolderSearch = {}): Observable<FolderPage> {
    const params = this.searchParams({page: search.page, size: search.size, sort: search.sort, order: search.order});
    return this.withUri('/pending', uri => this.httpClient.get<FolderPage>(uri, {params})).pipe(
      tap(page => this.pendingCount$.next(page.totalElements))
    );
  }

  getFolder(id: number): Observable<Folder> {
    return this.withUri('/' + id, uri => this.httpClient.get<Folder>(uri));
  }

  createFolder(folder: FolderSave): Observable<Folder> {
    return this.withUri('', uri => this.httpClient.post<Folder>(uri, folder));
  }

  updateFolder(id: number, folder: FolderSave): Observable<Folder> {
    return this.withUri('/' + id, uri => this.httpClient.put<Folder>(uri, folder));
  }

  deleteFolder(id: number): Observable<void> {
    return this.withUri('/' + id, uri => this.httpClient.delete<void>(uri));
  }

  duplicateFolder(id: number, name?: string): Observable<Folder> {
    return this.withUri('/' + id + '/duplicate', uri => this.httpClient.post<Folder>(uri, name ? {name} : {}));
  }

  addItems(id: number, items: FolderItem[]): Observable<SimpleFolder> {
    return this.withUri('/' + id + '/items', uri => this.httpClient.post<SimpleFolder>(uri, {items}));
  }

  removeItems(id: number, items: FolderItem[]): Observable<SimpleFolder> {
    return this.withUri('/' + id + '/items',
      uri => this.httpClient.request<SimpleFolder>('DELETE', uri, {body: {items}}));
  }

  /* -------------------- shares -------------------- */

  getShares(id: number): Observable<FolderShare[]> {
    return this.withUri('/' + id + '/shares', uri => this.httpClient.get<FolderShare[]>(uri));
  }

  share(id: number, email: string, permission: FolderPermission): Observable<FolderShare> {
    return this.withUri('/' + id + '/shares', uri => this.httpClient.post<FolderShare>(uri, {email, permission}));
  }

  updateShare(id: number, shareId: number, permission: FolderPermission): Observable<FolderShare> {
    return this.withUri('/' + id + '/shares/' + shareId, uri => this.httpClient.put<FolderShare>(uri, {permission}));
  }

  deleteShare(id: number, shareId: number): Observable<void> {
    return this.withUri('/' + id + '/shares/' + shareId, uri => this.httpClient.delete<void>(uri));
  }

  accept(id: number): Observable<void> {
    return this.withUri('/' + id + '/accept', uri => this.httpClient.post<void>(uri, null));
  }

  leave(id: number): Observable<void> {
    return this.withUri('/' + id + '/leave', uri => this.httpClient.post<void>(uri, null));
  }

  /* -------------------- export -------------------- */

  /** the preselected fields (defaultSelected) depend on the entries of the folder */
  getExportFields(id: number): Observable<ExportSection[]> {
    return this.httpClient.get<ExportSection[]>(this.exportBaseUri + '/' + id + '/fields');
  }

  exportFolder(id: number, request: FolderExportRequest): Observable<HttpResponse<Blob>> {
    return this.httpClient.post(this.exportBaseUri + '/' + id, request, {observe: 'response', responseType: 'blob'});
  }

  /* -------------------- admin -------------------- */

  getUsage(type: FolderItemType, id: number): Observable<FolderUsage> {
    const params = new HttpParams().set('type', type).set('id', String(id));
    return this.httpClient.get<FolderUsage>(this.usageUri, {params});
  }

  private withUri<T>(path: string, request: (uri: string) => Observable<T>): Observable<T> {
    return this.currentUserService.getUserId().pipe(
      switchMap(userId => request(this.usersBaseUri + userId + '/folders' + path))
    );
  }

  private searchParams(search: FolderSearch): HttpParams {
    let params = new HttpParams();
    Object.entries(search)
      .filter(([, value]) => value !== undefined && value !== null && value !== '')
      .forEach(([key, value]) => params = params.set(key, String(value)));
    return params;
  }
}
